import { useCallback, useEffect, useRef, useState } from "react";

// Define interfaces for TypeScript
interface UseAudioTranscriptionProps {
  continuous?: boolean;
  language?: string;
  interimResults?: boolean;
  maxAlternatives?: number;
  autoStart?: boolean;
}

interface UseAudioTranscriptionReturn {
  transcript: string;
  interimTranscript: string;
  isListening: boolean;
  startListening: () => void;
  stopListening: () => void;
  resetTranscript: () => void;
  error: Error | null;
  browserSupportsSpeechRecognition: boolean;
}

/**
 * A React hook that provides real-time audio transcription using the Web Speech API
 */
const useAudioTranscription = ({
  continuous = true,
  language = "en-US",
  interimResults = true,
  maxAlternatives = 1,
  autoStart = true,
}: UseAudioTranscriptionProps = {}): UseAudioTranscriptionReturn => {
  const [transcript, setTranscript] = useState<string>("");
  const [interimTranscript, setInterimTranscript] = useState<string>("");
  const [isListening, setIsListening] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [
    browserSupportsSpeechRecognition,
    setBrowserSupportsSpeechRecognition,
  ] = useState<boolean>(false);

  // Use a ref to maintain a reference to the recognition instance
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  // Setup for Web Speech API
  useEffect(() => {
    // Check for browser support
    const SpeechRecognitionAPI =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognitionAPI) {
      setBrowserSupportsSpeechRecognition(false);
      setError(
        new Error(
          "Your browser does not support the Web Speech API. Try Chrome or Edge.",
        ),
      );
      return;
    }

    setBrowserSupportsSpeechRecognition(true);

    // Initialize SpeechRecognition
    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = continuous;
    recognition.lang = language;
    recognition.interimResults = interimResults;
    recognition.maxAlternatives = maxAlternatives;

    // Set up event listeners
    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      setError(new Error(`Speech recognition error: ${event.error}`));
      setIsListening(false);
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let finalTranscript = "";
      let currentInterimTranscript = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;

        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          currentInterimTranscript += transcript;
        }
      }

      setTranscript((prev) => prev + finalTranscript);
      setInterimTranscript(currentInterimTranscript);
    };

    // Store recognition instance in ref
    recognitionRef.current = recognition;

    // Auto-start if enabled
    if (autoStart) {
      try {
        console.log("Webspeech: Auto Starting recognition");
        recognition.start();
        setIsListening(true);
      } catch (err) {
        if (err instanceof Error) {
          setError(err);
        } else {
          setError(new Error("An unknown error occurred"));
        }
      }
    }

    // Cleanup when component unmounts
    return () => {
      console.log("Cleaning up recognition instance");
      if (recognition) {
        try {
          recognition.stop();
          setIsListening(false);
        } catch (err) {
          console.error("Error stopping recognition:", err);
          // Ignore errors on cleanup
        }
      }
    };
  }, [autoStart, continuous, interimResults, language, maxAlternatives]);

  // Methods to control the transcription
  const startListening = useCallback(() => {
    console.log("Webspeech: Starting recognition");

    if (!recognitionRef.current) return;

    try {
      recognitionRef.current.start();
    } catch (err) {
      if (err instanceof Error) {
        setError(err);
      } else {
        setError(new Error("An unknown error occurred"));
      }
    }
  }, []);

  const stopListening = useCallback(() => {
    console.log("Webspeech: Stopping recognition");
    if (!recognitionRef.current) return;
    try {
      recognitionRef.current.stop();
    } catch (err) {
      if (err instanceof Error) {
        setError(err);
      } else {
        setError(new Error("An unknown error occurred"));
      }
    }
  }, []);

  const resetTranscript = useCallback(() => {
    setTranscript("");
    setInterimTranscript("");
  }, []);

  return {
    transcript,
    interimTranscript,
    isListening,
    startListening,
    stopListening,
    resetTranscript,
    error,
    browserSupportsSpeechRecognition,
  };
};

export default useAudioTranscription;
